import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  Upload,
  Download,
  Server,
  Globe,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Bot,
  Zap,
  Copy,
  RefreshCw,
  Loader2,
  Terminal
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ThreatProtection({ user, logout, token }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [agents, setAgents] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [logContent, setLogContent] = useState("");
  const [generatedFixes, setGeneratedFixes] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState("all");
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [dashboardRes, agentsRes, analysesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/threats/dashboard`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/agents`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/threats/analyses`, { headers, withCredentials: true })
      ]);
      setDashboard(dashboardRes.data);
      setAgents(agentsRes.data);
      setAnalyses(analysesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogContent(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleAnalyzeLogs = async () => {
    if (!logContent.trim()) {
      toast.error("Please enter or upload log content");
      return;
    }

    setAnalyzing(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${BACKEND_URL}/api/threats/analyze-logs`,
        { log_content: logContent, log_type: "auto" },
        { headers, withCredentials: true }
      );
      
      toast.success(`Analysis complete: ${response.data.threats_found} threats detected`);
      fetchData();
      setActiveTab("analyses");
    } catch (error) {
      toast.error("Failed to analyze logs");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateFixes = async (blockedIps) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${BACKEND_URL}/api/threats/generate-fixes`,
        { blocked_ips: blockedIps },
        { 
          headers, 
          withCredentials: true,
          params: { format: selectedFormat }
        }
      );
      setGeneratedFixes(response.data);
      toast.success("Security fixes generated!");
    } catch (error) {
      toast.error("Failed to generate fixes");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const downloadFix = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: "text-red-500 bg-red-500/10 border-red-500/20",
      high: "text-orange-500 bg-orange-500/10 border-orange-500/20",
      medium: "text-yellow-500 bg-yellow-500/10 border-yellow-500/20",
      low: "text-blue-500 bg-blue-500/10 border-blue-500/20"
    };
    return colors[severity] || colors.low;
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-primary">Loading threat protection...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="threat-protection-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold font-[Chivo]">AI Bot Threat Protection</h2>
            <p className="text-muted-foreground">
              Detect and block malicious bots and vulnerability scanners
            </p>
          </div>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Threats</p>
                  <p className="text-3xl font-bold font-[Chivo] text-red-500">
                    {dashboard?.total_threats || 0}
                  </p>
                </div>
                <ShieldAlert className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Unique Attackers</p>
                  <p className="text-3xl font-bold font-[Chivo] text-orange-500">
                    {dashboard?.unique_attackers || 0}
                  </p>
                </div>
                <Bot className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Critical Threats</p>
                  <p className="text-3xl font-bold font-[Chivo] text-red-500">
                    {dashboard?.severity_breakdown?.critical || 0}
                  </p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Agents</p>
                  <p className="text-3xl font-bold font-[Chivo] text-emerald-500">
                    {dashboard?.active_agents || 0}
                  </p>
                </div>
                <ShieldCheck className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="analyze">Analyze Logs</TabsTrigger>
            <TabsTrigger value="analyses">Past Analyses</TabsTrigger>
            <TabsTrigger value="agents">Protection Agents</TabsTrigger>
            <TabsTrigger value="fixes">Generate Fixes</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Threats */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-[Chivo]">Recent Threats</CardTitle>
                  <CardDescription>Latest detected malicious activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px] pr-4">
                    {dashboard?.recent_threats?.length === 0 ? (
                      <div className="text-center py-8">
                        <ShieldCheck className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
                        <p className="text-muted-foreground">No threats detected</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {dashboard?.recent_threats?.map((threat, i) => (
                          <div 
                            key={i}
                            className={`p-3 border rounded-sm ${getSeverityColor(threat.severity)}`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-sm">{threat.source_ip}</p>
                                <p className="text-xs opacity-80">{threat.threat_type}</p>
                              </div>
                              <Badge className={getSeverityColor(threat.severity)}>
                                {threat.severity}
                              </Badge>
                            </div>
                            {threat.signature_matched && (
                              <p className="text-xs mt-2 opacity-70">
                                Signature: {threat.signature_matched}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Top Threat Types */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-[Chivo]">Top Threat Types</CardTitle>
                  <CardDescription>Most common attack vectors</CardDescription>
                </CardHeader>
                <CardContent>
                  {dashboard?.top_threat_types?.length === 0 ? (
                    <div className="text-center py-8">
                      <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No data yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {dashboard?.top_threat_types?.map((type, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-sm bg-primary/10 flex items-center justify-center">
                              <Bot className="h-4 w-4 text-primary" />
                            </div>
                            <span className="text-sm capitalize">{type.type.replace(/_/g, ' ')}</span>
                          </div>
                          <span className="font-bold">{type.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analyze Logs Tab */}
          <TabsContent value="analyze" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-[Chivo]">Analyze Server Logs</CardTitle>
                <CardDescription>
                  Upload or paste your web server logs to detect threats
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".log,.txt"
                    className="hidden"
                  />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Log File
                  </Button>
                  <span className="text-sm text-muted-foreground self-center">
                    Supports Apache, Nginx, and common log formats
                  </span>
                </div>
                
                <Textarea
                  placeholder="Paste your server logs here (Apache/Nginx combined log format)..."
                  value={logContent}
                  onChange={(e) => setLogContent(e.target.value)}
                  rows={15}
                  className="font-mono text-sm"
                />
                
                <Button 
                  onClick={handleAnalyzeLogs} 
                  disabled={analyzing || !logContent.trim()}
                  className="glow-primary"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Analyze for Threats
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Past Analyses Tab */}
          <TabsContent value="analyses" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-[Chivo]">Analysis History</CardTitle>
              </CardHeader>
              <CardContent>
                {analyses.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No analyses yet</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setActiveTab("analyze")}
                    >
                      Analyze Your First Log
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {analyses.map((analysis, i) => (
                      <motion.div
                        key={analysis.analysis_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="p-4 border border-border rounded-sm hover:border-primary/30 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Analysis {analysis.analysis_id}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(analysis.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-bold text-lg">{analysis.threats_found}</p>
                              <p className="text-xs text-muted-foreground">threats found</p>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                const ips = analysis.summary?.recommended_blocks || [];
                                if (ips.length > 0) {
                                  handleGenerateFixes(ips);
                                  setActiveTab("fixes");
                                } else {
                                  toast.info("No IPs to block from this analysis");
                                }
                              }}
                            >
                              Generate Fixes
                            </Button>
                          </div>
                        </div>
                        {analysis.summary && (
                          <div className="mt-3 flex gap-4 text-sm">
                            <span className="text-red-500">
                              {analysis.summary.critical_threats} critical
                            </span>
                            <span className="text-orange-500">
                              {analysis.summary.high_threats} high
                            </span>
                            <span className="text-yellow-500">
                              {analysis.summary.medium_threats} medium
                            </span>
                            <span className="text-muted-foreground">
                              {analysis.summary.unique_ips} unique IPs
                            </span>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Protection Agents Tab */}
          <TabsContent value="agents" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-[Chivo]">Real-Time Protection Agents</CardTitle>
                    <CardDescription>
                      Install agents on your servers for 24/7 automated protection
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => window.open(`${BACKEND_URL}/api/agent/download`, '_blank')}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Agent
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {agents.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-border rounded-sm">
                    <Server className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Agents Installed</h3>
                    <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                      Install our protection agent on your servers for real-time threat detection 
                      and automated blocking.
                    </p>
                    <div className="bg-muted p-4 rounded-sm max-w-lg mx-auto text-left">
                      <p className="font-mono text-sm mb-2"># Quick Install</p>
                      <code className="text-xs text-primary">
                        wget {BACKEND_URL}/api/agent/download -O securescan_agent.py<br/>
                        pip install requests watchdog<br/>
                        sudo python3 securescan_agent.py
                      </code>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {agents.map((agent) => (
                      <div 
                        key={agent.agent_id}
                        className={`p-4 border rounded-sm ${
                          agent.status === 'active' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${
                              agent.status === 'active' ? 'bg-emerald-500/20' : 'bg-muted'
                            }`}>
                              <Server className={`h-5 w-5 ${
                                agent.status === 'active' ? 'text-emerald-500' : 'text-muted-foreground'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium">{agent.hostname}</p>
                              <p className="text-sm text-muted-foreground">{agent.ip_address}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right text-sm">
                              <p>{agent.stats?.total_blocked || 0} blocked</p>
                              <p className="text-muted-foreground">{agent.stats?.total_threats || 0} threats</p>
                            </div>
                            <Badge className={
                              agent.status === 'active' 
                                ? 'bg-emerald-500/10 text-emerald-500' 
                                : 'bg-muted text-muted-foreground'
                            }>
                              {agent.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Generate Fixes Tab */}
          <TabsContent value="fixes" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-[Chivo]">Security Fix Generator</CardTitle>
                <CardDescription>
                  Generate ready-to-use security configurations for your servers
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!generatedFixes ? (
                  <div className="text-center py-8">
                    <Terminal className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-4">
                      Analyze logs first, then generate fixes from the analysis results
                    </p>
                    <Button variant="outline" onClick={() => setActiveTab("analyze")}>
                      Go to Log Analysis
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Select format" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Formats</SelectItem>
                          <SelectItem value="iptables">iptables</SelectItem>
                          <SelectItem value="nginx">Nginx</SelectItem>
                          <SelectItem value="apache">Apache (.htaccess)</SelectItem>
                          <SelectItem value="cloudflare">Cloudflare</SelectItem>
                          <SelectItem value="aws_waf">AWS WAF</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {Object.entries(generatedFixes).map(([type, content]) => (
                      <div key={type} className="border border-border rounded-sm">
                        <div className="flex items-center justify-between p-4 border-b border-border">
                          <h4 className="font-medium capitalize">{type.replace(/_/g, ' ')}</h4>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyToClipboard(content)}
                            >
                              <Copy className="h-4 w-4 mr-1" />
                              Copy
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => downloadFix(content, `securescan_${type}.txt`)}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                        <pre className="p-4 text-xs font-mono overflow-x-auto max-h-64 overflow-y-auto">
                          {content}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
